import React, { useEffect, useState } from "react";
import { BarChart3, CheckCircle2, Percent, TrendingUp, Users } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="glass rounded-2xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-white/55 text-xs uppercase tracking-wider font-semibold">
      <Icon className="w-3.5 h-3.5 text-brand-orange" /> {label}
    </div>
    <div className="mt-2 font-display font-bold text-2xl text-white">{value}</div>
  </div>
);

const DistList = ({ title, data }) => {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="glass rounded-2xl p-5">
      <div className="text-white/55 text-xs uppercase tracking-wider font-semibold mb-3">{title}</div>
      {entries.length === 0 ? (
        <div className="text-white/40 text-sm font-mono">—</div>
      ) : (
        <div className="space-y-2">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 text-sm">
              <span className="w-32 truncate text-white/75">{k}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/[0.06]">
                <div className="h-full rounded-full bg-brand-orange" style={{ width: `${(v / max) * 100}%` }} />
              </div>
              <span className="w-8 text-right font-mono text-white/60">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function AdminDashboardPage() {
  const { locale } = useI18n();
  const isDE = locale === "de";
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/funnel/stats"), api.get("/leads")])
      .then(([s, l]) => {
        setStats(s.data);
        setLeads(l.data);
      })
      .catch((err) => {
        console.warn("Admin data load failed", err);
        setError(isDE ? "Daten konnten nicht geladen werden." : "Failed to load data.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-[80vh] bg-brand-ink px-6 md:px-12 lg:px-24 pt-28 pb-20" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-12 h-px bg-brand-orange" />
          <span className="label-mono">Admin</span>
        </div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-white tracking-tight mb-8">
          {isDE ? "Leads & Funnel-Übersicht" : "Leads & funnel overview"}
        </h1>

        {error && <div className="glass rounded-2xl p-5 text-red-400 text-sm mb-6" data-testid="admin-error">{error}</div>}

        {stats && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label={isDE ? "Chat-Gespräche" : "Conversations"} value={stats.total_conversations} testId="stat-total" />
              <StatCard icon={CheckCircle2} label={isDE ? "Abgeschlossen" : "Completed"} value={stats.completed_conversations} testId="stat-completed" />
              <StatCard icon={Percent} label={isDE ? "Abschlussquote" : "Completion rate"} value={`${Math.round(stats.completion_rate * 100)}%`} testId="stat-rate" />
              <StatCard icon={TrendingUp} label={isDE ? "Top-Service" : "Top service"} value={stats.most_selected_service || "—"} testId="stat-top-service" />
            </div>

            <div className="glass rounded-2xl p-5 mt-4" data-testid="funnel-table">
              <div className="flex items-center gap-2 text-white/55 text-xs uppercase tracking-wider font-semibold mb-3">
                <BarChart3 className="w-3.5 h-3.5 text-brand-orange" /> {isDE ? "Funnel-Schritte (erreicht / abgesprungen)" : "Funnel steps (reached / dropped)"}
              </div>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {stats.funnel.map((f) => (
                  <div key={f.step} className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-2.5 text-center">
                    <div className="text-[10px] font-mono text-white/45 truncate">{f.step}</div>
                    <div className="font-display font-bold text-white text-lg">{f.reached}</div>
                    <div className={`text-[10px] font-mono ${f.dropped_off_here > 0 ? "text-red-400" : "text-white/30"}`}>-{f.dropped_off_here}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <DistList title={isDE ? "Services" : "Services"} data={stats.service_distribution} />
              <DistList title="Budget" data={stats.budget_distribution} />
              <DistList title="Timeline" data={stats.timeline_distribution} />
            </div>
          </>
        )}

        <h2 className="font-display font-bold text-xl text-white tracking-tight mt-12 mb-4">
          {isDE ? `Leads${leads ? ` (${leads.length})` : ""}` : `Leads${leads ? ` (${leads.length})` : ""}`}
        </h2>
        {leads === null ? (
          <div className="text-white/50 text-sm font-mono">{isDE ? "Lädt…" : "Loading…"}</div>
        ) : (
          <div className="glass rounded-2xl overflow-x-auto" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/45 text-xs uppercase tracking-wider border-b border-white/[0.08]">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Service</th>
                  <th className="px-4 py-3 font-semibold">Budget</th>
                  <th className="px-4 py-3 font-semibold">{isDE ? "Quelle" : "Source"}</th>
                  <th className="px-4 py-3 font-semibold">{isDE ? "Datum" : "Date"}</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/[0.04] text-white/75 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-medium text-white">{l.name}</td>
                    <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="text-brand-orange hover:underline">{l.email}</a></td>
                    <td className="px-4 py-3">{l.service || "—"}</td>
                    <td className="px-4 py-3">{l.budget || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${l.source === "chatbot" ? "bg-brand-azure/15 text-brand-azure" : "bg-brand-orange/12 text-brand-orange"}`}>
                        {l.source === "chatbot" ? "Chat" : "Form"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-white/50 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-white/40 font-mono">{isDE ? "Noch keine Leads" : "No leads yet"}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
